import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Offcanvas,
  Row,
} from "react-bootstrap";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Controller, useForm, useWatch, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { IconPlus, IconTrash, IconUserPlus, IconX } from "@tabler/icons-react";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import "react-tagsinput/react-tagsinput.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { toast } from "sonner";
import { getCities, getStates, getCountries } from "../../Utils/hooks/location";
import { GetRequest, PutRequest } from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { usePostRequest } from "../../Utils/hooks/QueryHandler";
import Select, { components } from "react-select";
import { UsersPopup } from "../../pages/UsersPopup";
import { customStyles } from "../../Utils/helpers/commonhelper";
import { ContactForm } from "../education/Contacts/ContactModule/ContactForm";
import { CreateProductsModal } from "../../pages/CreateProductsModal";

const formSchema = (isEditMode) =>
  z.object({
    deal_name: z.string().min(1, "Deal Name is Required"),
    currency_id: z.string().min(1, "Currency is Required"),
    contact_name: z.string().min(1, "Contact name is required"),
    pipeline: z.string().min(1, "Pipeline is required"),
    pipeline_stage: z.string().min(1, "Pipeline stage is required"),
    owner: z.string().min(1, "Owner is required"),
    expected_closure_date: z.string().nonempty("Date is required"),
    expected_closure_time: z.string().nonempty("Time is required"),
    requirement: z.string().min(1, "Requirement is required"),
    product_services: z
      .array(
        z.object({
          product_id: z.string().min(1, "Product is required"),
          quantity: z
            .string()
            .min(1, "Quantity is required")
            .refine((val) => Number(val) !== 0, {
              message: "Quantity cannot be 0",
            }),

          budget: z.preprocess(
            (val) => (typeof val === "number" && Number.isNaN(val) ? undefined : val),
            z.number({
              required_error: "Budget is required",
              invalid_type_error: "Budget is required",
            }).int().min(0, "Budget must be 0 or more")
          )
        })
      )
      .min(1, "At least one product item is required"),
    priority: z.string().min(1, "Priority is required"),
    lead_source: isEditMode
      ? z.string().optional()
      : z.string().min(1, "Lead source is required"),
    follow_up_date: isEditMode
      ? z.string().optional()
      : z.string().min(1, "Date is required"),
    follow_up_time: isEditMode
      ? z.string().optional()
      : z.string().min(1, "Time is required"),
    feedback: isEditMode
      ? z.string().optional()
      : z
        .string()
        .min(1, "Feedback is required")
        .min(5, "Feedback is too short (minimum 5 characters)"),
  });

const getCurrentTime = () => {
  const now = new Date();
  return now.toTimeString().slice(0, 5);
};

const getTomorrowDate = () => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return t.toISOString().slice(0, 10);
};

const productSchema = z.object({
  title: z.string().min(1, "Title is required"),
  price: z
    .string()
    .min(1, "Price is required")
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
      message: "Price must be a valid number and at least 0",
    }),
  gst: z.string().min(1, "GST is Required!"),
  hascCode: z.string().min(1, "HASC Code is required"),
});

//menulist for add new products and services button in dropdown
const MenuList = (props) => {
  const { show, canCreate, moduleType } = props?.selectProps

  return (
    <>
      <components.MenuList {...props}>
        {props.children}
        {
          show &&
          <div
            style={{
              padding: "10px",
              borderTop: "1px solid #ddd",
              textAlign: "center",
              cursor: "pointer",
              color: "#0d6efd",
              fontWeight: 500,
            }}
            onClick={() => {
              if (moduleType === "contact" && !canCreate) {
                toast.warning("You are not authorized for this action!")
                return
              } else if (moduleType === "lead_source" && !canCreate) {
                toast.warning("You are not authorized for this action!")
                return
              }
              // trigger your modal open here
              props.selectProps.onAddNew();
            }}
          >
            + Add New
          </div>
        }
      </components.MenuList>
    </>
  );
};
// Dynamic Product Fields Component with forwardRef
const DynamicProductFields = React.forwardRef(
  (
    {
      control,
      register,
      setValue,
      watch,
      clearErrors,
      trigger,
      errors,
      products,
      setShowModal,
    },
    ref
  ) => {
    const { fields, append, remove } = useFieldArray({
      control,
      name: "product_services",
    });

    // Add initial field if none exist
    React.useEffect(() => {
      if (fields.length === 0) {
        append({ product_id: "", quantity: 1, budget: 0 });
      }
    }, [fields.length, append]);

    // Expose method to handle new product from modal
    React.useImperativeHandle(ref, () => ({
      addNewProductFromModal: (productId, price) => {
        const currentFields = watch("product_services") || [];

        // Check if there's only one field and it's empty
        const isOnlyDefaultField =
          currentFields.length === 1 &&
          (!currentFields[0].product_id || currentFields[0].product_id === "");

        if (isOnlyDefaultField) {
          // Set to the first field if it's empty
          setValue(`product_services.0.product_id`, String(productId));
          setValue(
            `product_services.0.budget`,
            Number(price),
            { shouldValidate: true }
          );
          clearErrors(`product_services.0.budget`);
        } else {
          // Add as new field if products already exist
          append({
            product_id: String(productId),
            quantity: 1,
            budget: Number(price),
          });
        }
      },
    }));

    const addMoreFields = () => {
      append({ product_id: "", quantity: 1, budget: 0 });
    };

    const removeFields = (index) => {
      if (fields.length > 1) {
        remove(index);
      }
    };
    const fieldsArr = useWatch({ control, name: 'product_services' }) || [];

    return (
      <div>
        {fields.map((field, index) => (
          <div key={field.id} className={index > 0 ? "" : ""}>
            <Row>
              {/* Product Selection */}
              <Col md={6}>
                <Form.Group className="mb-3 focus-z-index position-relative" controlId={`product-${index}`}>
                  <Form.Label>
                    Product and Services <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    name={`product_services.${index}.product_id`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        className="basic-single"
                        options={products.map((product) => ({
                          value: product.id,
                          label: product.service
                        }))}
                        value={products.length ? products.find(p => String(p.id) === String(field.value)) ? {
                          value: field.value,
                          label: products.find(p => String(p.id) === String(field.value))?.service
                        } : null : null}
                        onChange={(selectedOption) => {
                          const selectedId = selectedOption ? selectedOption.value : "";
                          field.onChange(selectedId);

                          if (selectedId) {
                            const selectedProduct = products.find((p) =>
                              String(p.id) === String(selectedId)
                            );
                            if (selectedProduct) {
                              setValue(
                                `product_services.${index}.budget`,
                                Number(selectedProduct.price),
                                { shouldValidate: true }
                              );
                              clearErrors(`product_services.${index}.budget`);
                            }
                          } else {
                            setValue(
                              `product_services.${index}.budget`,
                              0,
                              { shouldValidate: true }
                            );
                            clearErrors(`product_services.${index}.budget`);
                          }
                        }}
                        styles={customStyles}
                        isSearchable={true}
                        isClearable={true}
                        classNamePrefix="react-select"
                        components={{ MenuList }}
                        show={true}
                        onAddNew={() => setShowModal(true)}
                        placeholder="Select Product and services"
                      />
                    )}
                  />
                  {errors.product_services?.[index]?.product_id && (
                    <span className="text-danger fs-12 mt-1">
                      {errors.product_services[index].product_id.message}
                    </span>
                  )}
                </Form.Group>
              </Col>
              {/* Quantity */}
              <Col md={3}>
                <Form.Group className="mb-3 focus-z-index position-relative" controlId={`quantity-${index}`}>
                  <Form.Label>Quantity <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Quantity"
                    {...register(`product_services.${index}.quantity`)}
                  />
                  {errors.product_services?.[index]?.quantity && (
                    <span className="text-danger fs-12 mt-1">
                      {errors.product_services[index].quantity.message}
                    </span>
                  )}
                </Form.Group>
              </Col>
              {/* Budget */}
              <Col md={3}>
                <Form.Group className="mb-3 d-flex align-items-center  focus-z-index position-relative" controlId={`budget-${index}`}>
                  <div className="w-100">
                    <Form.Label>
                      Budget <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter Budget"
                      step="0.01"
                      defaultValue={field.budget ?? 0}
                      {...register(`product_services.${index}.budget`, {
                        setValueAs: (v) => (v === "" ? undefined : Number(v))
                      })}
                    />
                    {errors.product_services?.[index]?.budget && (
                      <span className="text-danger fs-12 mt-1">
                        {errors.product_services[index].budget.message}
                      </span>
                    )}
                  </div>

                  {/* Remove button (only for rows after first) */}
                  {index > 0 && (
                    <button
                      type="button"
                      style={{
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "0.4px solid red",
                        borderRadius: "50%",
                        cursor: "pointer",
                        marginLeft: "8px",
                        marginBottom: "4px"
                      }}
                      onClick={() => removeFields(index)}
                    >
                      <IconTrash
                        stroke={2}
                        style={{ stroke: "red" }}
                        className="w-3 h-3"
                      />
                    </button>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        ))}

        {/* Add More Button */}
        <div className="text-end mb-3">
          <button
            type="button"
            style={{
              fontSize: "14px",
              border: 0,
              color: "#777",
              background: "none",
            }}
            onClick={addMoreFields}
          >
            Add Product
          </button>
          <IconPlus stroke={2} style={{ width: '17px', height: '17px' }} ></IconPlus>
        </div>

        {/* Show validation error for the entire array */}
        {
          errors.product_services && !Array.isArray(errors.product_services) && (
            <div className="text-danger text-center fs-12 mt-1">
              {errors.product_services.message}
            </div>
          )
        }
      </div >
    );
  }
);

export const CreateDeal = ({
  show,
  onHide,
  editId = null,
  stageId = null,
  pipelineId = null,
  refetchDeals,
  contactId,
  setDealAdd,
  setDealClose,
  moduleName,
  id,
  contactArrCompany,
}) => {

  // Main create/edit lead form component
  const [contactDetails, setContactDetails] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [latestProductId, setLatestProductId] = useState(null);

  const [country, setCountries] = useState([{ countryName: "", countryId: 0 }]);
  const [states, setStates] = useState([{ stateName: "", stateCode: 0 }]);
  const [city, setCity] = useState([{ cityName: "", cityCode: 0 }]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [leadSource, setLeadSource] = useState([]);
  const [pipeLine, setPineLineStage] = useState([]);
  const [isContactSelected, setIsContactSelected] = useState(false);
  const [expectedDate, setExpectedDate] = useState("");
  const [expectedTime, setExpectedTime] = useState("");
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [currencyOpt, setCurrencyOpt] = useState([]);


  const [isSaving, setIsSaving] = useState(false)
  // new contact add form state
  const [contactShow, setShowContact] = useState(false)
  // state for recent contact added directly from add new
  const [recentContact, setRecentContact] = useState({
    recentContactId: "",
    recentContactName: "",
  });
  // new lead source form show
  const [sourceShow, setSourceShow] = useState(false)
  const [recentSource, setRecentSource] = useState({
    recentSourceId: "",
    recentSourceName: "",
  });
  const [selectedPipelineId, setSelectedPipelineId] = useState(
    pipelineId || null
  );
  const [availablePipelines, setAvailablePipelines] = useState([]);

  // Create ref for DynamicProductFields
  const productFieldsRef = useRef(null);

  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const customer_id = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const navigate = useNavigate();
  const extras = { customer, setCustomer, rf, navigate };

  const loginUserName = `${customer?.customer_details?.first_name || ""} ${customer?.customer_details?.last_name || ""
    }`;
  const loginUserId = `${customer?.customer_details?.id || ""}`;
  // toggle state for hiding the unrequired sections 
  const [showExtraSections, setShowExtraSections] = useState(true);
  const priorityOptions = [
    { id: 'low', label: 'Low' },
    { id: 'medium', label: 'Medium' },
    { id: 'high', label: 'High' },
  ];


  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema(isEditMode)),
    defaultValues: {
      contact_name: id?.toString() || "",
      owner: ownerOptions.length > 0 ? ownerOptions[0].id : null,
      product_services: [],
      expected_closure_date: getTomorrowDate(),
      expected_closure_time: getCurrentTime(),
      follow_up_date: getTomorrowDate(),
      follow_up_time: getCurrentTime(),
      priority: priorityOptions[0].id,
    },
  });

  const customContactOptions = useMemo(
    () =>
      contactOptions.map((conta) => ({
        value: conta.id,
        label: conta.label,
        company_name: conta.company
      })),
    [contactOptions]
  );
  // Custom option component for better formatting (optional)
  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <div>
          <div>{props.data.label}</div>
          {props.data.company_name && (
            <div style={{ fontSize: '0.85em', color: '#666' }}>
              {props.data.company_name}
            </div>
          )}
        </div>
      </components.Option>
    );
  };

  // permission setup for leads
  const permission = customer.customer_details?.permission
  // contact grant for create contact through lead 
  const contactPermssion = permission?.filter(c => c.module_name === "contact")
  const leadSourcePermission = permission?.filter(c => c.module_name === "setting")
  const user_type = Number(customer.customer_details?.user_type)

  // company permissions while creating contact
  const companyPermission = permission?.filter(c => c.module_name === "company")
  const companyGrants = {
    isCreate: user_type !== 1 ? Boolean(Number(companyPermission?.find(c => c.feature_name === "company")?.create)) : true,
  }

  const dealContactGrant = {
    isCreate: user_type !== 1 ? Boolean(Number(contactPermssion?.find(c => c.feature_name === "contact")?.create)) : true,
  }
  const dealMasterGrant = {
    isCreate: user_type !== 1 ? Boolean(Number(leadSourcePermission?.find(c => c.feature_name === "master")?.create)) : true,
  }

  const selectedContactName = useWatch({ control, name: "contact_name" });
  const feedbackValue = useWatch({ control, name: "feedback" });

  // Watch all form values to detect changes
  const watchedValues = watch();

  // Check if form has changes compared to original data
  useEffect(() => {
    // For new leads, check if any field has been filled
    const hasData = Object.keys(watchedValues).some((key) => {
      const value = watchedValues[key];
      if (key === "product_services") {
        return (
          value &&
          value.length > 0 &&
          value.some((item) => item.product_id || item.quantity || item.budget)
        );
      }
      return value && value !== "" && key !== "owner";
    });
    setHasFormChanges(hasData);
  }, [watchedValues, originalFormData]);

  // Clear errors when fields are properly filled
  useEffect(() => {
    if (feedbackValue && feedbackValue.length >= 5) {
      clearErrors("feedback");
    }
  }, [feedbackValue, clearErrors]);

  // Fetch available pipelines
  const fetchPipelines = async () => {
    try {
      // const res = await GetRequest(`/leads/fetchPipelines/${customer_id}/deals`, { headers }, extras)
      const res = await GetRequest(
        `/lead/fetchPipelines/${customer_id}/deals/${customer.customer_details?.id}/${customer.customer_details?.user_type}`,
        { headers },
        extras
      );
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }

      setAvailablePipelines(res.data || []);

      // Only set default pipeline if no pipeline is selected and it's not edit mode
      if (!selectedPipelineId && res.data?.length > 0) {
        const activePipeline =
          res.data.find((p) => p.is_active === "yes") || res.data[0];
        setSelectedPipelineId(activePipeline.id);
        setValue("pipeline", String(activePipeline.id));
      }
    } catch (error) {
      console.error("Failed to fetch pipelines:", error);
    }
  };
  useEffect(() => {
    if (show && customer_id) {
      fetchPipelines();
    }
  }, [show, customer_id, editId]);

  // Initialize form based on edit mode and stage selection
  useEffect(() => {
    if (contactId) {
      setIsContactSelected(true);
    } else if (!recentContact.recentContactId && !recentSource.recentSourceId) {
      if (moduleName !== "contact") {
        setIsContactSelected(false);
        setSelectedContactId("");
        setContactDetails(null);
        setSelectedContactId("");
        setExpectedDate(getTomorrowDate());
        setExpectedTime(getCurrentTime());
        setHasFormChanges(false);
        setOriginalFormData(null);
      }
      setSelectedContactId(id);

      // Set default owner
      const firstOwnerId =
        ownerOptions.length > 0 ? ownerOptions[0].id : loginUserId;

      const defaultValues = {
        owner: firstOwnerId,
        pipeline: pipelineId,
        pipeline_stage: stageId ? String(stageId) : "",
        contact_name: id?.toString() || "",
        expected_closure_date: getTomorrowDate(),
        expected_closure_time: getCurrentTime(),
        requirement: "",
        product_services: [],
        lead_source: "",
        follow_up_date: getTomorrowDate(),
        follow_up_time: getCurrentTime(),
        priority: priorityOptions[0].id,
        feedback: "",
      };
      reset(defaultValues);

      // Only set pipeline if explicitly passed as prop
      if (pipelineId && !selectedPipelineId) {
        setSelectedPipelineId(pipelineId);
        setValue("pipeline", String(pipelineId));
      }
    }
  }, [editId, stageId, reset, ownerOptions,
    recentContact.recentContactId,
    recentSource.recentSourceId,
  ]);

  useEffect(() => {
    if (moduleName !== "contact") {
      setIsContactSelected(!!selectedContactName && selectedContactName !== "");
    }
  }, [selectedContactName]);

  // Scroll to specific section in form
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const formContainer = document.querySelector(".form-container");
      formContainer.scrollTo({
        top: element.offsetTop - 110,
        behavior: "smooth",
      });
    }
  };

  const { mutate: addLead } = usePostRequest(
    `deals/createDeal`,
    null,
    { headers },
    extras
  );

  const fecthPipeLine = async () => {
    try {
      let pipelineIdToUse;
      if (pipelineId) {
        pipelineIdToUse = pipelineId;
      } else {
        pipelineIdToUse = selectedPipelineId;
      }

      const res = await GetRequest(
        `/lead/fetchPipelineStage/${pipelineIdToUse}/${customer_id}`,
        { headers },
        extras
      );
      if (res.status === "success") setPineLineStage(res.data);
    } catch (err) {
      console.error("Failed to fetch pipeline stages:", err);
    }
  };

  // Refetch pipeline stages when selected pipeline changes
  useEffect(() => {
    if (selectedPipelineId && customer_id && show) {
      fecthPipeLine();
      // Reset pipeline stage selection when pipeline changes
      setValue("pipeline_stage", "");
    }
  }, [selectedPipelineId, customer_id, show, pipelineId]);

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setIsSaving(true)
      const payload = {
        customer_id: customer_id,
        owner_id: data.owner,
        deal_name: data.deal_name?.trim(),
        currency_id: data.currency_id,
        contact_id: selectedContactId,
        pipeline_id: selectedPipelineId,
        pipeline_stage_id: data.pipeline_stage,
        expected_closure_date: data.expected_closure_date,
        expected_closure_time: data.expected_closure_time,
        requirement: data.requirement,
        product_services: data.product_services, // Full array for future use
        priority: data.priority,
        lead_source_id: data.lead_source,
        feedback: data.feedback?.trim(),
        followup_date: data.follow_up_date,
        followup_time: data.follow_up_time,
        user_login_id: loginUserId,
      };

      addLead(payload, {
        onSuccess: (res) => {
          if (res?.status === "success") {
            toast.success(res.message || "Deal added successfully");;

            refetchDeals && refetchDeals();
            if (moduleName === "contact") {
              setDealAdd(res.data)
            }
            setIsSaving(false)
            handleClose();
          } else {
            toast.error(res.message || "failed to add deal");
            setIsSaving(false)
          }
        },
        onError: (err) => {
          toast.error(err?.message || "add deal request failed");
          setIsSaving(false)
        },
      });
    } catch (err) {
      console.error("Error submitting form:", err);
      setIsSaving(false)
    }
  };

  // Fetch owner options when component mounts
  useEffect(() => {
    const fetchOwnerOptions = async () => {
      try {
        const res = await GetRequest(
          `/users/users/fetchUserAsOwner/${customer_id}`,
          { headers },
          extras
        );

        let data = [];

        if (res?.status === "success") {
          data = res.data.map((user) => ({
            id: user.id,
            label: user.full_name,
          }));
        }

        const isLoginUserIncluded = data.some(
          (user) => user.id === loginUserId
        );

        if (!isLoginUserIncluded) {
          data.unshift({ id: loginUserId, label: loginUserName });
        }

        setOwnerOptions(data);
        // Set first owner as default
        if (data.length > 0) {
          setValue("owner", data[0].id);
        }
      } catch (error) {
        const fallback = [{ id: loginUserId, label: loginUserName }];
        setOwnerOptions(fallback);
        setValue("owner", loginUserId);
      }
    };

    if (show) {
      fetchOwnerOptions();
    }
  }, [customer, show]);

  // Fetch contact options when form opens
  useEffect(() => {
    const fetchContactNames = async () => {
      const res = await GetRequest(`lead/getContactsForLeads/${customer_id}/${loginUserId}/${customer.customer_details?.user_type}`, headers, extras)

      if (res.status === "success") {
        const data = res.data.map((contactName) => ({
          id: contactName.id,
          label: contactName.contact_full_name,
          company: contactName.company_name
        }))

        setContactOptions(data)
        res.data &&
          res.data.find((c) => {
            if (c.id === recentContact.recentContactId) {
              setValue("contact_name", recentContact.recentContactId);
              setSelectedContactId(recentContact.recentContactId)
              setRecentContact((prev) => ({
                ...prev,
                recentContactName: c.contact_full_name,
              }));
            }
          });
      }
    }

    if (show || recentContact.recentContactId) {
      fetchContactNames()
    }
  }, [show, recentContact.recentContactId])

  // Fetch contact details by ID
  const fetchContactDetailById = async (contactSelected = contactId !== undefined ? contactId : selectedContactId) => {
    if (!contactSelected) {
      setContactDetails(null)
      return;
    }
    try {
      const res = await GetRequest(`lead/getContactDetailsForLeads/${contactSelected}`, headers, extras)
      if (res.status === "success" && res.data?.length > 0) {
        setContactDetails(res.data[0])
        setIsContactSelected(true)
      }
    } catch (error) {
      return;
    }
  }

  // Fetch contact details by ID
  useEffect(() => {
    fetchContactDetailById()
  }, [selectedContactId, recentContact.recentContactId])
  // Fetch countries when component mounts
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await getCountries();
        if (data) {
          setCountries(
            data.map((c) => ({
              countryName: c.name,
              countryId: c.id,
            }))
          );
        }
      } catch (err) {
        return
      }
    };
    if (show) {
      fetchCountries();
    }
  }, [show]);

  // useeeffect for fetching currency value dropdown.
  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `deals/fetchActiveCurrency/${customer_id}`,
        { headers },
        extras
      );
      if (res.status === "fail" || res.success === false) {
        return;
      }

      const currencyOptions =
        res.data &&
        res.data
          ?.map((item) => {
            try {
              // Parse the currency string to object
              const currencyObj = JSON.parse(item.currency);

              // Create React Select option
              return {
                value: item.id, // Using the item's id as value
                label: `${currencyObj.label} (${currencyObj.value})`, // Format: "Chilean Peso (CLP)"
              };
            } catch (error) {
              console.error("Error parsing currency:", error);
              return null;
            }
          })
          .filter((option) => option !== null); // Remove any null entries from failed parsing

      setCurrencyOpt(currencyOptions);
    })();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (!contactDetails?.country) return;

    const fetchStates = async () => {
      try {
        const data = await getStates(contactDetails.country);
        if (data) {
          setStates(
            data.map((s) => ({
              stateName: s.state_name,
              stateCode: s.id,
            }))
          );
        }
      } catch (err) {
        // Error handling
        return
      }
    };
    fetchStates();
  }, [contactDetails?.country]);
  useEffect(() => {
    if (moduleName === "contact") {
      setIsContactSelected(true);
    } else {
      setIsContactSelected(false);
    }
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const data = await getCities(contactDetails.state);
        if (data) {
          const mapped = data.map((city) => ({
            cityName: city.city_name,
            cityCode: city.id,
          }));

          setCity(mapped);
        }
      } catch (error) {
        toast.error("Error Fetching Cities");
        return
      }
    };

    if (contactDetails?.state) fetchCities();
  }, [contactDetails?.state]);

  // Helper functions to get location names
  const getCountryName = (id) => {
    const found = country.find((c) => String(c.countryId) === String(id));
    return found ? found.countryName : "";
  };

  const getStateName = (id) => {
    const found = states.find((s) => String(s.stateCode) === String(id));
    return found ? found.stateName : "";
  };

  const getCityName = (id) => {
    const found = city.find((c) => String(c.cityCode) === String(id));
    return found ? found.cityName : "";
  };

  // Fetch products when form opens
  const fetchProducts = async () => {
    try {
      const res = await GetRequest(
        `masters/getProducts/${customer_id}`,
        { headers },
        extras
      );

      if (res?.data) {
        // const data = res.data && res.data?.map(c => ({ value: c.id, label: c.service, price: c.price }))
        setProducts(res.data)
      }
    } catch (error) {
      // Error handling
      return
    }
  };

  // Fetch lead sources when form opens
  const fetchLeadSorce = async () => {
    try {
      const res = await GetRequest(`masters/getAllLead_source/${customer_id}`, { headers }, extras)
      if (res?.data) setLeadSource(res.data)
      res.data &&
        res.data.find((c) => {
          if (c.id === String(recentSource.recentSourceId)) {
            setValue("lead_source", recentSource.recentSourceId);
            setRecentSource((prev) => ({
              ...prev,
              recentSourceName: c.source_title,
            }));
          }
        });
    } catch (error) {
      // Error handling
      return;
    }
  }

  // Fetch products and lead sources when form opens
  useEffect(() => {
    if (customer_id && show) {
      fetchProducts();
      fetchLeadSorce();
    }
  }, [customer_id, show, recentSource.recentSourceId]);

  // Fetch pipeline stages when form opens
  useEffect(() => {
    if (customer_id && show) {
      fecthPipeLine();
    }
  }, [customer_id, pipelineId, show]);

  // Handle form close
  const handleClose = () => {
    if (!editId) {
      const firstOwnerId =
        ownerOptions.length > 0 ? ownerOptions[0].id : loginUserId;
      reset({
        owner: firstOwnerId,
        deal_name: "",
        pipeline_stage: "",
        contact_name: "",
        expected_closure_date: getTomorrowDate(),
        expected_closure_time: getCurrentTime(),
        requirement: "",
        product_services: [],
        lead_source: "",
        follow_up_date: getTomorrowDate(),
        follow_up_time: getCurrentTime(),
        priority: priorityOptions[0].id,

        feedback: "",
      });
      setContactDetails(null);
      setSelectedContactId("");
      setExpectedDate(getTomorrowDate());
      setExpectedTime(getCurrentTime());
      if (moduleName !== "contact") {
        setIsContactSelected(false);
      }
      setIsEditMode(false);
    }
    // setLatestProductId(null);
    setHasFormChanges(false);
    setOriginalFormData(null);
    clearErrors();
    setIsSaving(false)
    onHide();
  };

  // Generate sidebar items based on contact selection and edit mode
  const getSidebarItems = () => {
    const baseItems = [
      { id: "Requirment", label: "Requirement", show: true },
      { id: "BasicInformation", label: "General Information", show: true },
    ];

    if (isContactSelected) {
      baseItems.push(
        { id: "Communication", label: "Communication", show: !showExtraSections ? true : false },
        { id: "Location", label: "Location", show: !showExtraSections ? true : false },
        { id: "Social", label: "Social", show: !showExtraSections ? true : false },
        {
          id: "BussinessInformation",
          label: "Business Information",
          show: !showExtraSections ? true : false,
        }
      );

      if (!isEditMode) {
        baseItems.push({
          id: "FollowUpInformation",
          label: "FollowUp Information",
          show: true,
        });
      }
    }

    return baseItems.filter((item) => item.show);
  };
  return (
    <>
      <style>
        {`
        .w-75::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .w-75::-webkit-scrollbar-track {
          background: transparent;
        }
        .w-75::-webkit-scrollbar-thumb {
          background-color: rgb(122, 58, 94);
          border-radius: 4px;
        }
        .w-75::-webkit-scrollbar-thumb:hover {
          background-color: rgb(102, 48, 79);
        }
        
        /* Fixed form container size */
        .form-container {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }
        
        /* Fixed offcanvas width */
        .offcanvas.w-75 {
          width: 75% !important;
          max-width: 75% !important;
          min-width: 75% !important;
        }
      `}
      </style>
      <Offcanvas
        className="w-75"
        placement="end"
        backdrop="static"
        enforceFocus={true}
        show={show}
      >
        <Offcanvas.Header className="border-bottom d-flex justify-content-between align-items-center border-dashed pt-3 pb-0">
          <div className="pt-0 ps-4">

            <Offcanvas.Title
              style={{ color: "rgb(7, 145, 255)", fontWeight: 500 }}
              className="addEnquiryHead"
            >
              Add Deal
            </Offcanvas.Title>
            <p
              style={{ fontSize: "14px", color: "#b26792" }}
              className="text-secondary"
            >
              Add potential lead here, Fill all the mandatory fields before
              submitting the lead.
            </p>

          </div>
          <div className="d-flex align-items-baseline gap-2">
            <div className=" ">
              <Form.Check
                type="switch"
                id="toggle-extra"
                label="Required Fields."
                checked={showExtraSections}
                onChange={() => setShowExtraSections(!showExtraSections)}
              />
            </div>
            <button style={{ cursor: "pointer", border: "none", background: "transparent" }}
              onClick={() => {
                handleClose()
              }}
            > <IconX size={20} stroke={2} /></button>
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div style={{ display: "flex", height: "100%" }}>
            {/* Sidebar */}
            <div
              style={{ width: "280px", minWidth: "280px", maxWidth: "280px" }}
              className="border-end"
            >
              <ul className="ps-3 create-deal-sidebar" style={{ listStyle: "none" }}>
                {getSidebarItems().map((item, index) => (
                  <li
                    key={item.id}
                    className="position-relative border-start ps-4 py-2"
                    style={{
                      cursor: "pointer",
                      opacity: item.show ? 1 : 0.5,
                      pointerEvents: item.show ? "auto" : "none",
                    }}
                  >
                    <span
                      className="position-absolute start-0 translate-middle rounded-circle"
                      style={{
                        width: 10,
                        height: 10,
                        top: index === 0 ? 20 : "50%",
                        backgroundColor: "rgb(7, 145, 255)",
                        border: "1px solid rgb(7, 145, 255)",
                      }}
                    />

                    <a
                      onClick={() => scrollToSection(item.id)}
                      style={{
                        color: "#676262",
                        textDecoration: "none",
                        fontSize: "14px",
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <Form
              className="form-container"
              onSubmit={handleSubmit(onSubmit)}
              style={{
                margin: 0,
                overflowY: "auto",
                height: "100%",
                paddingLeft: "20px",
                flex: 1,
                minWidth: 0,
                paddingBottom: "5rem",
              }}
              encType="multipart/form-data"
            >
              <div>
                {/* Requirement Section */}
                <h4 id="Requirment" className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}>
                  Requirement
                </h4>

                <Form.Group
                  className="mb-3 position-relative focus-z-index d-flex flex-column"
                  controlId="formDealName"
                >
                  <Form.Label
                    className="w-auto"
                    style={{ marginBottom: "2px", marginTop: "2px" }}
                  >
                    Deal Name <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control type="text" {...register("deal_name")} />
                  </InputGroup>
                  {errors.deal_name && (
                    <span className="text-danger mt-1 fs-12">
                      {errors.deal_name.message}
                    </span>
                  )}
                </Form.Group>
                <Form.Group
                  className="mb-3 position-relative focus-z-index d-flex flex-column"
                  controlId="formCurrency"
                >
                  <Form.Label
                    className="w-auto"
                    style={{ marginBottom: "2px", marginTop: "2px" }}
                  >
                    Currency <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    name="currency_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        styles={customStyles}
                        {...field}
                        options={currencyOpt}
                        value={
                          field.value
                            ? currencyOpt.find(
                              (option) => option.value === field.value
                            ) || null
                            : null
                        }
                        onChange={(selectedOption) => {
                          field.onChange(
                            selectedOption ? selectedOption.value : ""
                          );
                        }}
                        onBlur={field.onBlur}
                        isSearchable={true}
                        isClearable={true}
                        classNamePrefix="react-select"
                        placeholder="Select Currency.."
                      />
                    )}
                  />
                  {errors.currency_id && (
                    <span className="text-danger mt-1 fs-12">
                      {errors.currency_id.message}
                    </span>
                  )}
                </Form.Group>

                <Form.Group
                  className="mb-3 position-relative focus-z-index"
                  controlId="requirement"
                >
                  <Form.Label className="w-auto">
                    Requirement <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter requirement"
                    {...register("requirement", {
                      required: "Requirement is required",
                    })}
                  />
                  {errors.requirement && (
                    <span className="text-danger fs-12 mt-1">
                      {errors.requirement.message}
                    </span>
                  )}
                </Form.Group>

                {/* Dynamic Product Fields */}
                <DynamicProductFields
                  ref={productFieldsRef}
                  control={control}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  clearErrors={clearErrors}
                  trigger={trigger}
                  errors={errors}
                  products={products}
                  setShowModal={setShowModal}
                />
              </div>
              <hr />
              <div style={{ paddingTop: 10 }}>
                <h4 id="BasicInformation" className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}>
                  General Information
                </h4>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter w-auto">
                        Contact Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="contact_name"
                        control={control}
                        render={({ field }) => {
                          if (moduleName === "contact") {
                            return (
                              <Select
                                className="basic-single"
                                value={{
                                  value: id,
                                  label: contactOptions.find((c) => c.id === id)?.label,
                                }}
                                isDisabled
                                aria-readonly="true"
                                classNamePrefix="react-select"
                                styles={{
                                  ...customStyles, pointerEvents: "none",
                                }}
                              />
                            );
                          }
                          const options = moduleName === "deal"
                            ? contactOptions.map(contact => ({
                              value: contact.id,
                              label: contact.label,
                              company_name: contact.company
                            }))
                            : moduleName === "company"
                              ? contactArrCompany.map(contact => ({
                                value: contact.id,
                                label: `${contact.first_name} ${contact.last_name}`
                              }))
                              : [];

                          const currentValue = watch("contact_name");
                          const selectedOption = currentValue ? {
                            value: currentValue,
                            label: options.find(opt => opt.value === currentValue)?.label || "Select Contact Name"
                          } : null;

                          return (
                            <Select
                              className="basic-single"
                              options={options}
                              value={selectedOption}
                              onChange={(selectedOption) => {
                                const newValue = selectedOption ? selectedOption.value : "";

                                setValue("contact_name", newValue, { shouldValidate: true });
                                setSelectedContactId(newValue);

                                if (newValue) {
                                  clearErrors("contact_name");
                                }
                              }}
                              isSearchable={true}
                              isClearable={true}
                              components={{
                                MenuList,
                                Option: moduleName === "deal" ? CustomOption : components.Option
                              }}
                              onAddNew={() => {
                                setShowContact(true);
                                setDealClose && setDealClose(false)
                              }}
                              show={moduleName === "company" ? false : true}
                              classNamePrefix="react-select"
                              styles={customStyles}
                              placeholder="Select Contact Name"
                              moduleType={"contact"}
                              canCreate={dealContactGrant.isCreate}
                            />
                          );
                        }}
                      />

                      {errors.contact_name && (
                        <p className="text-danger fs-12 mt-1">Contact name is required</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter">
                        Pipeline <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="pipeline"
                        control={control}
                        rules={{ required: "Pipeline is required" }}
                        render={({ field }) => {
                          const selectedOption = availablePipelines.find(
                            (p) => String(p.id) === String(field.value)
                          );

                          return (
                            <Select
                              styles={customStyles}
                              options={availablePipelines.map((pipeline) => ({
                                value: String(pipeline.id),
                                label: pipeline.pipeline_name,
                              }))}
                              value={
                                selectedOption
                                  ? {
                                    value: String(selectedOption.id),
                                    label: selectedOption.pipeline_name,
                                  }
                                  : null
                              }
                              onChange={(selected) => {
                                const newPipelineId = selected
                                  ? Number(selected.value)
                                  : null;
                                field.onChange(
                                  selected ? selected.value : null
                                );
                                setSelectedPipelineId(newPipelineId);
                                // Reset pipeline stage when pipeline changes
                                setValue("pipeline_stage", "");
                                if (selected) {
                                  clearErrors("pipeline");
                                }
                              }}
                              isSearchable
                              isClearable
                              placeholder="Select Pipeline"
                              classNamePrefix="react-select"
                            />
                          );
                        }}
                      />
                      {errors.pipeline && (
                        <p className="text-danger fs-12 mt-1">Pipeline is required</p>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter">
                        Pipeline Stage<span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="pipeline_stage"
                        control={control}
                        rules={{ required: "Pipeline stage is required" }}
                        render={({ field }) => {
                          const selectedOption = pipeLine.find(
                            (p) => String(p.id) === String(field.value)
                          );
                          return (
                            <Select
                              styles={customStyles}
                              options={pipeLine.map((stage) => ({
                                value: String(stage.id),
                                label: stage.stage_name,
                              }))}
                              value={
                                selectedOption
                                  ? {
                                    value: String(selectedOption.id),
                                    label: selectedOption.stage_name,
                                  }
                                  : null
                              }
                              onChange={(selected) => {
                                field.onChange(
                                  selected ? selected.value : null
                                );
                                if (selected) {
                                  clearErrors("pipeline_stage");
                                }
                              }}
                              isSearchable
                              isClearable
                              placeholder={
                                selectedPipelineId
                                  ? "Select Pipeline Stage"
                                  : "Select Pipeline First"
                              }
                              classNamePrefix="react-select"
                              isDisabled={
                                !selectedPipelineId || pipeLine.length === 0
                              }
                            />
                          );
                        }}
                      />
                      {/* Show error even when disabled if pipeline is selected but stage is not */}
                      {errors.pipeline_stage && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.pipeline_stage.message}
                        </p>
                      )}
                      {/* Show helper text when pipeline is not selected */}
                      {!selectedPipelineId && (
                        <p
                          className="text-danger mt-1"
                          style={{ fontSize: "0.875rem" }}
                        >
                          Please select a pipeline first
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <div className="row">
                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="w-auto">Owner</Form.Label>
                      <Controller
                        name="owner"
                        control={control}
                        rules={{ required: "Owner is required" }}
                        render={({ field }) => (
                          <Select
                            styles={{
                              ...customStyles,
                              menu: (provided) => ({
                                ...provided,
                                height: "85px",
                                overflow: "hidden",
                                zIndex: 9999
                              }),
                              menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                            }}
                            className="basic-single"
                            options={ownerOptions.map((owner) => ({
                              value: owner.id,
                              label: owner.label,
                            }))}
                            value={
                              watch("owner")
                                ? {
                                  value: watch("owner"),
                                  label:
                                    ownerOptions.find(
                                      (o) => o.id === watch("owner")
                                    )?.label || "Select Owner",
                                }
                                : null
                            }
                            onChange={(selectedOption) => {
                              // If cleared, set to first owner
                              const newValue = selectedOption
                                ? selectedOption.value
                                : ownerOptions.length > 0
                                  ? ownerOptions[0].id
                                  : "";
                              setValue("owner", newValue);
                            }}
                            isSearchable={true}
                            isClearable={true}
                            classNamePrefix="react-select"
                            placeholder="Select Owner"
                          />
                        )}
                      />
                      {errors.owner && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.owner.message}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index ">
                      <Form.Label className="fw-lighter">
                        Expected Closure On
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        {...register("expected_closure_date")}
                        value={expectedDate}
                        onChange={(e) => {
                          setExpectedDate(e.target.value);
                          setValue("expected_closure_date", e.target.value);
                          if (e.target.value) {
                            clearErrors("expected_closure_date");
                          }
                        }}
                      />
                      <div className="d-flex">
                        <div style={{ width: "65%" }}>
                          {errors.expected_closure_date && (
                            <p className="text-danger fs-12 mt-1">
                              {errors.expected_closure_date.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    {/* Expected Closure Time - 6 columns within this section */}
                    <Form.Group className="mb-3 position-relative focus-z-index ">
                      <Form.Label className="fw-lighter">
                        Expected Closure Time
                      </Form.Label>
                      <Form.Control
                        type="time"
                        {...register("expected_closure_time")}
                        value={expectedTime}
                        onChange={(e) => {
                          setExpectedTime(e.target.value);
                          setValue("expected_closure_time", e.target.value);
                          if (e.target.value) {
                            clearErrors("expected_closure_time");
                          }
                        }}
                      />
                      <div>
                        {errors.expected_closure_time && (
                          <p className="text-danger fs-12 mt-1">
                            {errors.expected_closure_time.message}
                          </p>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </div>
              </div>
              <hr />
              {/* {moduleName ==="contact" && setIsContactSelected(true)} */}
              {isContactSelected && !showExtraSections && (
                <>
                  {/* Communication Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4
                      id="Communication"
                      className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}
                    >
                      Communication
                    </h4>

                    <div className="pt-3 d-flex flex-column">
                      <div className="row">
                        {/* Primary Email */}
                        <div className="col-md-6">
                          <Form.Group
                            className="mb-3 position-relative focus-z-index col-md-12"
                            controlId="formEmail"
                          >
                            <Form.Label className="w-auto">
                              Email<span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Form.Select
                                value={
                                  JSON.parse(
                                    contactDetails?.user_email ?? "[]"
                                  )[0]?.email_type || "Office"
                                }
                                style={{ width: "30%", fontSize: "14px" }}
                                disabled
                              >
                                <option value="Office">Office</option>
                                <option value="Personal">Personal</option>
                              </Form.Select>
                              <InputGroup>
                                <Form.Label>
                                  Email Address{" "}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                  style={{
                                    height: "40px",
                                    border: "1px solid #c5c5c5",
                                  }}
                                  type="email"
                                  value={
                                    JSON.parse(
                                      contactDetails?.user_email ?? "[]"
                                    )[0]?.email || ""
                                  }
                                  disabled
                                  placeholder="ex: abc@gmail.com"
                                />
                              </InputGroup>
                            </div>
                            {errors.primary_email && (
                              <p className="text-danger fs-12 mt-1">
                                {errors.primary_email.message}
                              </p>
                            )}
                          </Form.Group>
                        </div>

                        {/* Phone */}
                        <div className="col-md-6">
                          <Form.Group
                            className="mb-3 position-relative focus-z-index"
                            controlId="formContact"
                          >
                            <Form.Label className="w-auto">
                              Contact <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Form.Select
                                value={
                                  JSON.parse(
                                    contactDetails?.user_phone ?? "[]"
                                  )[0]?.phone_type || "Mobile"
                                }
                                style={{ width: "30%", fontSize: "14px" }}
                                disabled
                              >
                                <option value="Mobile">Mobile</option>
                                <option value="Work">Work</option>
                                <option value="Home">Home</option>
                                <option value="Personal">Personal</option>
                              </Form.Select>

                              {(() => {
                                const phoneData = JSON.parse(contactDetails?.user_phone ?? "[]")[0] || {};
                                const fullNumber = `+${phoneData.country_code || "91"}${phoneData.phone || ""}`;

                                return (
                                  <InputGroup>
                                    <Form.Label>
                                      Mobile Number <span className="text-danger">*</span>
                                    </Form.Label>
                                    <PhoneInput
                                      country={"in"}
                                      countryCodeEditable={false}
                                      buttonStyle={{ border: "none" }}
                                      inputStyle={{
                                        width: "235px",
                                        height: "40px",
                                        border: "none",
                                      }}
                                      containerStyle={{
                                        borderRadius: "5px",
                                      }}
                                      disabled
                                      value={fullNumber}
                                      classNamePrefix="react-select"
                                      styles={customStyles}
                                    />
                                  </InputGroup>
                                );
                              })()}
                            </div>

                            {errors.primary_contact_number && (
                              <p className="text-danger fs-12 mt-1">
                                {errors.primary_contact_number.message}
                              </p>
                            )}
                          </Form.Group>
                        </div>
                      </div>

                      {/* Timezone */}
                      <div style={{ marginTop: "10px", width: "100%", height: "3rem" }}>
                        <Form.Group className="mb-3 focus-z-index position-relative" controlId="formTimezone">
                          <Form.Label className="w-auto">Select Timezone</Form.Label>
                          <Controller
                            name="timezone"
                            control={control}
                            render={({ field }) => (
                              <TimezoneSelect
                                value={JSON.parse(contactDetails?.timezone ?? "{}")}
                                onChange={() => { }}
                                isDisabled={true}
                                timezones={{
                                  ...allTimezones,
                                  "America/Lima": "Pittsburgh",
                                  "Europe/Berlin": "Frankfurt",
                                }}
                                styles={customStyles}
                              />
                            )}
                          />
                          {errors.timezone && <p className="text-danger fs-12 mt-1">{errors.timezone.message}</p>}
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                  <hr />

                  {/* Location Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4 id="Location" className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}>
                      Location
                    </h4>

                    <Form.Group
                      className="mb-3 d-flex flex-column position-relative focus-z-index"
                      controlId="formAddress"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="w-auto"
                      >
                        Address
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as="textarea"
                          value={contactDetails?.address || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </InputGroup>
                    </Form.Group>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCountryReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Country
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={getCountryName(contactDetails?.country) || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formStateReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          State
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={getStateName(contactDetails?.state) || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>
                    </div>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCityReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          City
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={getCityName(contactDetails?.city) || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formZipcodeReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Zipcode
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={contactDetails?.zipcode || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <hr />

                  {/* Social Links Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4 id="Social" className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}>
                      Social
                    </h4>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formFacebook"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Facebook
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.facebook || ""}
                            {...register("facebook")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formLinkedin"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          LinkedIn
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.linkedin || ""}
                            {...register("linkedin")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </div>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formInstagram"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Instagram
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.instagram || ""}
                            {...register("instagram")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formTwitter"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Twitter
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.twitter || ""}
                            {...register("twitter")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </div>
                  </div>
                  <hr />

                  {/* Business Information Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4
                      id="BussinessInformation"
                      className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}
                    >
                      Business Information
                    </h4>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCompanyName"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Company Name
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.company_name || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formIndustry"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Company Industry
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.industry || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formBusinessType"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Business Type
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.business_type || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCompanyWebsite"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Company Website
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.website || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group
                          className="mb-3 col-md-12 position-relative focus-z-index"
                          controlId="formEmail"
                        >
                          <Form.Label className="w-auto">Email</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              disabled
                              value={
                                JSON.parse(
                                  contactDetails?.user_email ?? "[]"
                                )[0]?.email_type || "Office"
                              }
                              style={{
                                width: "30%",
                                fontSize: "14px",
                                backgroundColor: "#e9ecef",
                              }}
                            >
                              <option value="Office">Office</option>
                            </Form.Select>
                            <Form.Control
                              type="email"
                              value={
                                JSON.parse(
                                  contactDetails?.user_email ?? "[]"
                                )[0]?.email || ""
                              }
                              readOnly
                              style={{ backgroundColor: "#e9ecef" }}
                            />
                          </div>
                        </Form.Group>
                      </div>

                      <div className="col-md-6">
                        <Form.Group
                          className="mb-3 position-relative focus-z-index"
                          controlId="formContact"
                        >
                          <Form.Label className="w-auto">Contact</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              disabled
                              value="Mobile"
                              style={{
                                width: "30%",
                                fontSize: "14px",
                              }}
                            >
                              <option value="Mobile">Mobile</option>
                            </Form.Select>
                            <PhoneInput
                              disabled
                              country={"in"}
                              value={contactDetails?.company_phones || ""}
                              inputStyle={{ width: "235px", height: "38px" }}
                              countryCodeEditable={false}
                              inputProps={{ readOnly: true }}
                              buttonStyle={{
                                backgroundColor: "#e9ecef",
                                border: "1px solid #ced4da",
                                borderRight: "none",
                                borderRadius: "4px 0 0 4px",
                              }}
                            />
                          </div>
                        </Form.Group>
                      </div>
                    </div>

                    <Form.Group
                      className="mb-3 d-flex flex-column position-relative focus-z-index"
                      controlId="formAddress"
                    >
                      <Form.Label
                        className="w-auto"
                      >
                        Company Address
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as="textarea"
                          value={contactDetails?.address || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </InputGroup>
                    </Form.Group>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCountry"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Country
                        </Form.Label>
                        <Form.Select
                          value={contactDetails?.company_country || ""}
                          disabled
                          style={{ backgroundColor: "#e9ecef" }}
                        >
                          <option value=""></option>
                          {country.map((c) => (
                            <option key={c.countryId} value={c.countryId}>
                              {c.countryName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formState"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          State
                        </Form.Label>
                        <Form.Select
                          value={contactDetails?.company_state || ""}
                          disabled
                          style={{ backgroundColor: "#e9ecef" }}
                        >
                          <option value=""></option>
                          {states.map((s, index) => (
                            <option key={index} value={s.stateCode}>
                              {s.stateName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCity"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          City
                        </Form.Label>
                        <Form.Select
                          value={contactDetails?.company_city || ""}
                          disabled
                          style={{ backgroundColor: "#e9ecef" }}
                        >
                          <option value=""></option>
                          {city.map((c, index) => (
                            <option key={index} value={c.cityCode}>
                              {c.cityName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formZipcode"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Zipcode
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={contactDetails?.company_zipcode || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <hr />


                </>
              )}

              {isContactSelected &&
                <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                  <h4
                    id="FollowUpInformation"
                    className="offcanvasSecHeading" style={{ marginLeft: 0, color: "rgb(7, 145, 255)" }}
                  >
                    FollowUp Information
                  </h4>

                  <div className="row">
                    <Form.Group
                      className="mb-3 col-md-3 position-relative focus-z-index"
                      controlId="formLeadSource"
                    >
                      <Form.Label
                        className="w-auto"
                        style={{ marginBottom: 1, marginLeft: "11px" }}
                      >
                        Lead Source <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="lead_source"
                        control={control}
                        rules={{
                          required: "lead_source   is required",
                        }}
                        render={({ field }) => (
                          <Select
                            className="basic-single"
                            options={leadSource.map((source) => ({
                              value: source.id,
                              label: source.source_title,
                            }))}
                            value={
                              watch("lead_source")
                                ? {
                                  value: watch("lead_source"),
                                  label:
                                    leadSource.find((l) => l.id === watch("lead_source"))?.source_title ||
                                    "Select Lead Source",
                                }
                                : null
                            }
                            onChange={(selectedOption) => {
                              const newValue = selectedOption ? selectedOption.value : ""
                              setValue("lead_source", newValue)
                              clearErrors("lead_source")
                            }}
                            isSearchable={true}
                            isClearable={true}
                            styles={customStyles}
                            components={{ MenuList }}
                            onAddNew={() => setSourceShow(true)}
                            classNamePrefix="react-select"
                            placeholder="Select lead source"
                            show={true}
                            moduleType={"lead_source"}
                            canCreate={dealMasterGrant.isCreate}
                          />
                        )}
                      />
                      {errors.lead_source && (
                        <span className="text-danger fs-12 mt-1">
                          {errors.lead_source.message}
                        </span>
                      )}
                    </Form.Group>
                    <Form.Group className="mb-3 col-md-3 position-relative focus-z-index" controlId="formPriority">
                      <Form.Label className="w-auto"
                        style={{ marginBottom: 1, marginLeft: "11px" }}>Priority</Form.Label>
                      <Controller
                        name="priority"
                        control={control}
                        rules={{ required: "Priority is required" }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Select
                            inputRef={ref}
                            options={priorityOptions.map((p) => ({ value: p.id, label: p.label }))}
                            value={priorityOptions
                              .map(p => ({ value: p.id, label: p.label }))
                              .find((opt) => opt.value === value) || null}
                            onChange={(val) => onChange(val ? val.value : "")}
                            isSearchable={true}
                            classNamePrefix="react-select"
                            styles={{
                              placeholder: (provided) => ({
                                ...provided,
                                paddingLeft: "11px",
                              }),
                              ...customStyles,
                            }}
                            placeholder="Select Priority"
                          />
                        )}
                      />
                      {errors.priority && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.priority.message}
                        </p>
                      )}
                    </Form.Group>

                    <Form.Group
                      className="mb-3 col-md-3 position-relative focus-z-index"
                      controlId="formFollowUp"
                    >
                      <Form.Label
                        className="w-auto"
                        style={{ marginBottom: 1, marginLeft: "11px" }}
                      >
                        Next Follow-up Date{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        {...register("follow_up_date", {
                          required: "Follow-up date is required",
                        })}
                      />
                      {errors.follow_up_date && (
                        <span className="text-danger fs-12 mt-1">
                          {errors.follow_up_date.message}
                        </span>
                      )}
                    </Form.Group>
                    <Form.Group
                      className="mb-3 col-md-3 position-relative focus-z-index"
                      controlId="formFollowUp"
                    >
                      <Form.Label
                        className="w-auto"
                        style={{ marginBottom: 1, marginLeft: "11px" }}
                      >
                        Next Follow-up Time{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="time"
                        {...register("follow_up_time", {
                          required: "Follow-up time is required",
                        })}
                      />
                      {errors.follow_up_time && (
                        <span className="text-danger fs-12 mt-1">
                          {errors.follow_up_time.message}
                        </span>
                      )}
                    </Form.Group>
                  </div>

                  <Form.Group
                    controlId="formFeedback"
                    className="position-relative focus-z-index"
                  >
                    <Form.Label className="w-auto">
                      Feedback<span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...register("feedback", {
                        required: "Feedback is required",
                        minLength: {
                          value: 5,
                          message:
                            "Feedback is too short (minimum 5 characters)",
                        },
                      })}
                    />
                    {errors.feedback && (
                      <span className="text-danger fs-12 mt-1" >
                        {errors.feedback.message}
                      </span>
                    )}
                  </Form.Group>
                </div>
              }

              <div
                className="sticky-buttons"
                style={{
                  position: "fixed",
                  width: "calc(75% - 300px)",
                  bottom: 0,
                  right: 0,
                  height: "80px",
                  zIndex: 2,
                  backgroundColor: "white",
                  borderTop: "1px solid #dee2e6",
                  paddingRight: "20px",
                  paddingLeft: "20px",
                }}
              >
                <div className="d-flex justify-content-end gap-4 pt-4">
                  <Button
                    onClick={handleClose}
                    className="btn btn-default-outline"
                    style={{ border: "1px solid black" }}
                  >
                    Cancel
                  </Button>
                  {editId ? (
                    <Button className="btn btn-default" type="submit" disabled={isSaving}>
                      {isSaving ? "Updating" : "Update"}
                    </Button>
                  ) : (
                    <Button className="btn btn-default" type="submit" disabled={isSaving}>
                      {isSaving ? "Saving" : "Save"}
                    </Button>
                  )}
                </div>
              </div>
            </Form>

            <CreateProductsModal
              showModal={showModal}
              setShowModal={setShowModal}
              productFieldsRef={productFieldsRef}
              products={products}
              fetchProducts={fetchProducts}
            />

          </div>
        </Offcanvas.Body >
      </Offcanvas >
      {contactShow &&
        <ContactForm
          onClose={() => setDealClose(false)}
          show={contactShow}
          setShow={setShowContact}
          setRecentContactId={setRecentContact}
          companyGrants={companyGrants}
          country={country}
          masterGrant={dealMasterGrant}
        />
      }
      <UsersPopup
        setRecentSourceId={setRecentSource}
        sourceShow={sourceShow}
        setSourceShow={setSourceShow}
      />
    </>
  );
};